import { useState } from "react"
import { useFeatureFlagEnabled } from "posthog-js/react"
import AdHocCommands from "@/api/adHocCommands"
import ShareRouteParser from "@/lib/shareRouteParser"
import {
  getTextSummary,
  LangTemplate,
  sumRouteMetrics,
} from "@/components/maps/mapUtils"
import { Route2g } from "@/reducers/routes2gSlice"
import RouteTextSummaryParser from "@/lib/routeTextSummaryParser"
import RouteGoogleLinkParser from "@/lib/routeGoogleLinkParser"
import { useAppDispatch, useAppSelector } from "@/reducers/hooks"
import { setComposerDraft } from "@/reducers/composerDraftsSlice"
import { selectAccountChat } from "@/reducers/accountChatsSelectors"
import { findProfile } from "@/reducers/profilesSliceSelectors"
import routeMessageTemplates from "@/lib/routeMessageTemplates.json"
import { useEventCallback } from "@/hooks/use-event-callback"
import { buildSharedRouteLink } from "@/lib/sharedRouteLink"
import { useStopToStopDistance } from "@/hooks/useStopToStopDistance"

export const useShareRoute = (
  route: Route2g | undefined,
  driverJid: string,
) => {
  const dispatch = useAppDispatch()
  const [sharing, setSharing] = useState(false)
  const isAISummaryEnabled = useFeatureFlagEnabled("ai-route-summary")
  const isGoogleRouteLinkEnabled = useFeatureFlagEnabled("google-route-link")
  const isHereWeGoShareLinkEnabled = useFeatureFlagEnabled(
    "herewego-share-link",
  )
  const accountChat = useAppSelector(selectAccountChat(driverJid))
  const driverProfile = useAppSelector(findProfile(accountChat?.driverA))
  const preferredLanguage = driverProfile?.preferredLanguage || "pl"
  const isDoubleCrew = accountChat?.group === "double_crew"
  const templates: LangTemplate =
    routeMessageTemplates[
      preferredLanguage as keyof typeof routeMessageTemplates
    ] || routeMessageTemplates.pl

  const hideCoordinates = Boolean(
    useFeatureFlagEnabled("hide_coordinates_in_route_messages"),
  )
  const hideStopTimes = Boolean(
    useFeatureFlagEnabled("hide_stop_times_in_route_messages"),
  )
  const showLegDistance = useStopToStopDistance()
  const share = useEventCallback(async () => {
    if (!route) return
    try {
      setSharing(true)
      window.analytics.track("RouteShared")
      const result = await AdHocCommands.shareRoute(window.XMPP!, driverJid)
      const secret = ShareRouteParser.parse(result)?.secret
      const { totalDistance } = sumRouteMetrics(route)
      const baseUrl =
        import.meta.env.MODE === "production" &&
        window.location.hostname !== "app.trickswift.eu"
          ? "https://app.slickshift.ai/"
          : "https://app.trickswift.eu/"

      const distance = `${(totalDistance / 1000).toFixed(0)} km`
      const link = buildSharedRouteLink(baseUrl, secret, { showLegDistance })
      const nonPassthroughStops = route.stops.filter(
        (stop) => stop.action !== "passthrough",
      )
      const textSummary = getTextSummary(
        nonPassthroughStops,
        undefined,
        templates,
        hideCoordinates,
        hideStopTimes,
      )
      const linkLabel = templates.openRouteInSlick || "🚛 Open route in Slick"
      let text = `${distance}\n\n${textSummary}\n\n${linkLabel}: ${link}`
      if (isHereWeGoShareLinkEnabled) {
        const hereWeGoLabel =
          templates.openRouteInHereWeGo || "🚚 Open route in HereWeGo"
        const hereWeGoLink = `${import.meta.env.REACT_APP_API_PUBLIC_BASE_URL}/shared_route_here?secret=${secret}`
        text = `${text}\n\n${hereWeGoLabel}: ${hereWeGoLink}`
      }
      if (isGoogleRouteLinkEnabled) {
        const routeGoogleLinkResult = await AdHocCommands.getRouteGoogleLink(
          window.XMPP!,
          driverJid,
        )
        const routeGoogleLink = RouteGoogleLinkParser.parse(
          routeGoogleLinkResult,
        )?.googleLink
        text = `${text}\n\n${routeGoogleLink}`
      }

      if (isAISummaryEnabled) {
        const aiSummaryResult = await AdHocCommands.getRouteTextSummary(
          window.XMPP!,
          driverJid,
        )
        const aiTextSummary =
          RouteTextSummaryParser.parse(aiSummaryResult)?.textSummary
        text = `${distance}\n\n${aiTextSummary}\n\n${link}`
      }

      dispatch(
        setComposerDraft({
          chatJid: driverJid,
          surface: "driver",
          draft: {
            text,
            options: { skipRouteDetection: true, startTracking: true },
          },
        }),
      )
    } catch (e) {
      console.log(e)
    } finally {
      setSharing(false)
    }
  })

  return { share, sharing, isDoubleCrew }
}
